import Config from '../utils/Config';
import LocaleManager from '../utils/LocaleManager';
import SoundManager from '../utils/SoundManager';

class SlidePane extends Phaser.Group {

    constructor(game, slides)
    {
        super(game);

        if (!slides || !slides.length)
        {
            throw new Error('No slides.');
        }

        this.onCompleteScroll = new Phaser.Signal();

        this._slidesCount = slides.length;

        let slideWidth = 0;
        let slideHeight = 0;
        const slideViews = slides.map((slide) =>
        {
            const slideView = this.makeSlide(slide);
            slideWidth = Math.max(slideWidth, slideView.width);
            slideHeight = Math.max(slideHeight, slideView.height);
            return slideView;
        });

        this._slideWidth = slideWidth;
        this._slideHeight = slideHeight;

        const slidesContainer = this._slidesContainer = new Phaser.Group(game, null);
        slideViews.reduce((dX, slide) =>
        {
            slide.position.x = dX + (slideWidth - slide.width >> 1);
            dX += slideWidth;
            slidesContainer.addChild(slide);
            return dX;
        }, 0);

        const slidesContainerMask = new Phaser.Graphics(game, 0, 0);
        slidesContainerMask.beginFill(0, 0);
        slidesContainerMask.drawRect(0, 0, slideWidth, slideHeight);
        slidesContainerMask.endFill();
        this.addChild(slidesContainerMask);

        slidesContainer.visible = false;
        setTimeout(() =>
        {
            slidesContainer.mask = slidesContainerMask;
            slidesContainer.visible = true;
        }, 100);

        this.addChild(slidesContainer);
    }

    get width()
    {
        return this._slideWidth;
    }

    get height()
    {
        return this._slideHeight;
    }

    makeSlide({ imageKey, descKey })
    {
        const game = this.game;
        const slide = new Phaser.Group(game, null);

        const image = new Phaser.Image(game, 0, 0, imageKey);
        const desc = new Phaser.Text(game, 0, 0, LocaleManager.getString(descKey), {
            font: Config.DEFAULT_FONT,
            fontSize: 20,
            fill: '#FFFFFF',
            fontWeight: 'normal',
            wordWrap: true,
            wordWrapWidth: Math.floor(image.width * 0.8),
            align: 'center'
        });
        desc.lineSpacing = -5;
        desc.anchor.x = 0.5;
        desc.position.set(
            image.width >> 1,
            image.height
        );

        slide.addChild(image);
        slide.addChild(desc);

        return slide;
    }

    get slideIndex()
    {
        return this._slideIndex || 0;
    }

    set slideIndex(value)
    {

        let effectiveIndex = Math.min(this._slidesCount - 1, Math.max(0, value));
        this._slideIndex = effectiveIndex;
        const tween = this.game.add.tween(this._slidesContainer).to({ x: -this._slideWidth * effectiveIndex }, 500, 'Quart.easeOut');
        tween.start().onComplete.addOnce(() =>
        {
            this.onCompleteScroll.dispatch();
        });
    }

    next()
    {
        this.slideIndex++;
    }

    prev()
    {
        this.slideIndex--;
    }
}

class ProgressBar extends Phaser.Group {

    constructor(game, { width = 100, height = 4, min = 0, max = 100 } = {})
    {
        super(game);

        this.params = {
            width,
            min: Math.min(parseInt(min) || 0, parseInt(max) || 100),
            max: Math.max(parseInt(min) || 0, parseInt(max) || 100)
        };

        const bg = this._bg = new Phaser.Graphics(game, 0, 0);
        bg.beginFill(0x888888);
        bg.drawRect(0, 0, width, height);
        bg.endFill();
        this.addChild(bg);

        const line = this._line = new Phaser.Graphics(game, 0, 0);
        line.beginFill(0xFF0000);
        line.drawRect(0, 0, width, height);
        line.endFill();
        line.scale.x = 0.01;
        this.addChild(line);

        const label = this._label = new Phaser.Text(game, 0, 0, '0 / 0', {
            font: Config.DEFAULT_FONT,
            fontSize: 10,
            fontWeight: 'normal',
            fill: '#FFFFFF'
        });
        label.anchor.x = 0.5;
        label.position.set(width >> 1, height + 5);
        this.addChild(label);

        this.value = 0;
        this.label = '0 / 0';
    }

    get label()
    {
        return this._label.text || '';
    }

    set label(value)
    {
        this._label.text = value;
    }

    get value()
    {
        return this._value || this.params.min;
    }

    set value(value)
    {
        let { min, max } = this.params;

        this._value = Math.min(max, Math.max(min, value));

        const scale = this._value / (max - min);
        this._line.scale.x = Math.max(0.01, scale);
    }
}

export default class Intro extends Phaser.Group {

    constructor(game)
    {
        super(game);

        this.onSkip = new Phaser.Signal();

        const showTime = 7500;

        const slides = new SlidePane(game, [
            { imageKey: 'intro.slide1', descKey: '' },
            { imageKey: 'intro.slide2', descKey: 'intro_slide2' },
            { imageKey: 'intro.slide3', descKey: 'intro_slide3' }
        ]);

        this.addChild(slides);

        const controlsGroup = new Phaser.Group(game, null);
        this.addChild(controlsGroup);

        const skipButton = this.makeSkipButton();
        controlsGroup.addChild(skipButton);
        skipButton.onClick.addOnce(() =>
        {
            if (this._tween)
            {
                this._tween.stop();
            }

            this.onSkip.dispatch();
        });

        const progressBar = new ProgressBar(game);
        progressBar.position.set(
          skipButton.width - progressBar.width >> 1,
          skipButton.y + skipButton.height + 10
        );
        progressBar.value = 0;
        progressBar.label = '1 / 3';
        controlsGroup.addChild(progressBar);

        const nextButton = this.makeScrollButton('intro.arrow-right');
        nextButton.onClick.add(() =>
        {
            if (slides.slideIndex === 2)
            {
                skipButton.onClick.dispatch();
                return;
            }

            slides.next();
            progressBar.value = 0;
            progressBar.label = `${slides.slideIndex + 1} / 3`;

            if (this._tween)
            {
                this._tween.stop();
            }

            let tween = this._tween = game.add.tween(progressBar).to({ value: 100 }, showTime, Phaser.Easing.Linear.None);
            tween.start().onComplete.addOnce(() =>
            {
                nextButton.onClick.dispatch();
            });
        });
        controlsGroup.addChild(nextButton);

        const prevButton = this.makeScrollButton('intro.arrow-left');
        prevButton.onClick.add(() =>
        {
            slides.prev();
            progressBar.value = 0;
            progressBar.label = `${slides.slideIndex + 1} / 3`;

            if (this._tween)
            {
                this._tween.stop();
            }

            let tween = this._tween = game.add.tween(progressBar).to({ value: 100 }, showTime, Phaser.Easing.Linear.None);
            tween.start().onComplete.addOnce(() =>
            {
                nextButton.onClick.dispatch();
            });
        });
        controlsGroup.addChild(prevButton);

        prevButton.position.set(
            -prevButton.width - 20,
            skipButton.height - prevButton.height >> 1
        );

        nextButton.position.set(
            skipButton.width + 20,
            prevButton.y
        );

        controlsGroup.position.set(
            (slides.width - controlsGroup.width >> 1) - controlsGroup.getLocalBounds().x,
            slides.height - controlsGroup.getLocalBounds().y
        );

        this.position.set(
            Config.GAME_WIDTH - slides.width >> 1,
            Config.GAME_HEIGHT - this.height >> 1
        );

        let tween = this._tween = game.add.tween(progressBar).to({ value: 100 }, showTime, Phaser.Easing.Linear.None);
        tween.start().onComplete.addOnce(() =>
        {
            nextButton.onClick.dispatch();
        });
    }

    makeScrollButton(key)
    {
        const button = new Phaser.Image(this.game, 0, 0, key);
        button.inputEnabled = true;
        button.onClick = new Phaser.Signal();
        button.events.onInputUp.add(() =>
        {
            button.alpha = 1;
            button.onClick.dispatch();
        });
        button.events.onInputDown.add(() =>
        {
            button.alpha = 0.5;
        });

        return button;
    }

    makeSkipButton()
    {
        const game = this.game;
        const button = new Phaser.Group(game, null, 'skipButton');
        const label = new Phaser.Text(game, 0, 0, LocaleManager.getString('skip'), {
            font: Config.DEFAULT_FONT,
            fontWeight: 'normal',
            fill: '#FFFEFE',
            fontSize: 10
        });
        label.anchor.set(0.5);

        const bg = new Phaser.Graphics(game, 0, 0);
        bg.beginFill(0xFF0000);
        bg.drawRoundedRect(0, 0, label.width + 80, label.height + 5, 4);
        bg.endFill();
        bg.inputEnabled = true;
        bg.events.onInputUp.add(() =>
        {
            button.onClick.dispatch();
        });

        label.position.set(bg.width >> 1, (bg.height >> 1) + 3);

        button.addChild(bg);
        button.addChild(label);

        button.onClick = new Phaser.Signal();

        return button;
    }
}